import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tag, Spin, Pagination, Select, Avatar } from 'antd';
import { EnvironmentOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import s from './s.module.less';
import {
    TPracticeListCounts,
    TPracticeListFilter,
    TPracticeListItem,
    TPracticeListStatus,
    TextVisitServiceType,
} from 'types/textVisit';
import { TScopedProviderOption } from 'types/group';
import { getPracticeTextVisitList, getPracticeTextVisitCounts } from 'api/textVisit';
import { getScopedProviderOptions } from 'api/group';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import { getDetailText, getTimeLeft } from 'pages/dashboard/TextVisitPage/utils';
import TextVisitSubmittedDrawer from 'components/TextVisitSubmittedDrawer';
import RemindProviderModal from './RemindProviderModal';
import { FollowUpServiceTypeMap } from '../MyCaseCard';
import KlarityEmpty from 'assets/common/klarityEmpty.svg';
import DefaultIcon from 'assets/conditionIcon/default-condition.png';

dayjs.extend(utc);

const PAGE_SIZE = 10;
const ALL_PROVIDERS = 'all';

const STATUS_TABS: { label: string; key: TPracticeListStatus; countKey: keyof TPracticeListCounts }[] = [
    { label: 'In-progress', key: 'in-progress', countKey: 'inProgressCount' },
    { label: 'Active', key: 'active', countKey: 'activeCount' },
    { label: 'Closed', key: 'closed', countKey: 'closedCount' },
];

const formatDate = (date?: string) => (date ? dayjs.utc(date).local().format('MMM D, YYYY') : '');

const providerName = (item: TPracticeListItem) =>
    [item.assignedProviderFirstName, item.assignedProviderLastName].filter(Boolean).join(' ');

const PracticeView: React.FC = () => {
    const [status, setStatus] = useState<TPracticeListStatus>('in-progress');
    const [providerFilter, setProviderFilter] = useState<string>(ALL_PROVIDERS);
    const [currentPage, setCurrentPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [list, setList] = useState<TPracticeListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [counts, setCounts] = useState<TPracticeListCounts>({
        inProgressCount: 0,
        activeCount: 0,
        closedCount: 0,
    });

    const [providerOptions, setProviderOptions] = useState<TScopedProviderOption[]>([]);

    const [settings] = useSettings();
    const stateList = settings[EnumFields.STATE] || [];

    // case detail drawer
    const [selectedCase, setSelectedCase] = useState<TPracticeListItem>();
    const [showDetail, setShowDetail] = useState(false);

    // remind provider modal
    const [remindTarget, setRemindTarget] = useState<TPracticeListItem>();
    const [showRemind, setShowRemind] = useState(false);

    const providerId = useMemo(
        () => (providerFilter === ALL_PROVIDERS ? undefined : Number(providerFilter)),
        [providerFilter],
    );

    const buildFilter = useCallback(
        (page: number): TPracticeListFilter => ({
            status,
            providerId,
            current: page,
            size: PAGE_SIZE,
        }),
        [status, providerId],
    );

    const fetchList = useCallback(
        async (page: number) => {
            try {
                setLoading(true);
                const res = await getPracticeTextVisitList(buildFilter(page));
                if (!res.error && res?.data?.data) {
                    setList(res.data.data.list || []);
                    setTotal(res.data.data.totalRecords || 0);
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Failed to fetch practice text visit list:', e);
            } finally {
                setLoading(false);
            }
        },
        [buildFilter],
    );

    const fetchCounts = useCallback(async () => {
        try {
            const res = await getPracticeTextVisitCounts({ providerId });
            if (!res.error && res?.data?.data) {
                setCounts(res.data.data);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch practice text visit counts:', e);
        }
    }, [providerId]);

    const fetchProviderOptions = useCallback(async () => {
        try {
            const res = await getScopedProviderOptions();
            if (!res.error && res?.data?.data) {
                setProviderOptions(res.data.data || []);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch provider options:', e);
        }
    }, []);

    useEffect(() => {
        fetchProviderOptions();
    }, [fetchProviderOptions]);

    // reset to page 1 whenever the tab or provider filter changes
    useEffect(() => {
        setCurrentPage(1);
        fetchList(1);
        fetchCounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, providerId]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchList(page);
    };

    const statusTabs = STATUS_TABS.map((tab) => ({
        ...tab,
        count: counts[tab.countKey] ?? 0,
    }));

    const renderTimeInfo = (item: TPracticeListItem) => {
        if (status === 'closed') {
            return <div className={s.timeMain}>Closed on {formatDate(item.closedAt)}</div>;
        }
        const isPrescriptionRefill = item.serviceType === TextVisitServiceType.PRESCRIPTION_REFILL;
        const timeLeft = getTimeLeft(item.assignedProviderAt || '');
        return (
            <>
                <div className={s.timeMain}>Last updated: {formatDate(item.updatedAt)}</div>
                {!isPrescriptionRefill && item.assignedProviderAt && (
                    <span className={s.timeBadge} style={{ backgroundColor: timeLeft.color }}>
                        <ClockCircleOutlined />
                        <span>{timeLeft.text}</span>
                    </span>
                )}
            </>
        );
    };

    const renderActions = (item: TPracticeListItem) => {
        if (status === 'closed') {
            const refunded = !!item.refundStatus || (item.refundAmount ?? 0) > 0;
            return (
                <div className={s.actions}>
                    {refunded ? (
                        <span className={s.refundedBadge}>Refunded</span>
                    ) : (
                        <span className={s.paidBadge}>Paid</span>
                    )}
                    <button
                        type="button"
                        className={s.linkBtn}
                        onClick={() => {
                            setSelectedCase(item);
                            setShowDetail(true);
                        }}
                    >
                        View details
                    </button>
                </div>
            );
        }

        const waitingDays = item.patientSubmitAt ? dayjs().diff(dayjs.utc(item.patientSubmitAt).local(), 'day') : 0;
        return (
            <div className={s.actions}>
                {waitingDays > 0 && (
                    <span className={s.waitingBadge}>
                        <ClockCircleOutlined />
                        Waiting {waitingDays} {waitingDays === 1 ? 'day' : 'days'}
                    </span>
                )}
                <button
                    type="button"
                    className={s.linkBtn}
                    onClick={() => {
                        setRemindTarget(item);
                        setShowRemind(true);
                    }}
                >
                    Remind provider
                </button>
            </div>
        );
    };

    const renderSpecialty = (item: TPracticeListItem) => {
        const isFollowUpCase = item.serviceType !== TextVisitServiceType.INITIAL_VISIT;
        if (isFollowUpCase) {
            const followUp = FollowUpServiceTypeMap[item.serviceType as keyof typeof FollowUpServiceTypeMap];
            return (
                <div className={s.specialty}>
                    <span className={s.specialtyIcon}>
                        <img src={followUp?.icon} alt={followUp?.title} width={16} height={16} />
                    </span>
                    <span className={s.specialtyName}>{followUp?.title}</span>
                </div>
            );
        }
        return (
            <div className={s.specialty}>
                <span className={s.specialtyIcon}>
                    <img src={item.icon || DefaultIcon} alt={item.assessmentName} width={16} height={16} />
                </span>
                <span className={s.specialtyName}>{item.assessmentName || item.conditions}</span>
            </div>
        );
    };

    const renderRow = (item: TPracticeListItem) => (
        <div key={item.id} className={s.caseItem}>
            <div className={s.caseInfo}>
                <div className={s.patient}>
                    <div className={s.patientName}>
                        {[item.patientFirstName, item.patientLastName].filter(Boolean).join(' ')}
                    </div>
                    <div className={s.patientLoc}>
                        <EnvironmentOutlined />
                        <span>{getDetailText(item, stateList)}</span>
                    </div>
                </div>

                <div className={s.provider}>
                    <Avatar size={28} src={item.assignedProviderHeadshot || undefined}>
                        {providerName(item).charAt(0)}
                    </Avatar>
                    <span className={s.providerName}>{providerName(item)}</span>
                </div>

                {renderSpecialty(item)}

                <div className={s.time}>{renderTimeInfo(item)}</div>
            </div>

            {renderActions(item)}
        </div>
    );

    return (
        <div className={s.wrap}>
            <div className={s.filterRow}>
                <div className={s.pills}>
                    {statusTabs.map((tab) => (
                        <Tag.CheckableTag
                            key={tab.key}
                            checked={status === tab.key}
                            className={s.pill}
                            onClick={() => setStatus(tab.key)}
                        >
                            {tab.label} ({tab.count})
                        </Tag.CheckableTag>
                    ))}
                </div>

                <Select
                    className={s.providerSelect}
                    value={providerFilter}
                    onChange={setProviderFilter}
                    showSearch
                    optionFilterProp="label"
                    popupMatchSelectWidth={300}
                    options={[
                        { label: 'All providers', value: ALL_PROVIDERS },
                        ...providerOptions.map((p) => ({
                            label: [p.firstName, p.lastName].filter(Boolean).join(' '),
                            value: String(p.providerId),
                        })),
                    ]}
                />
            </div>

            <Spin spinning={loading}>
                {list.length > 0 ? (
                    <>
                        <div className={s.caseList}>{list.map(renderRow)}</div>
                        {total > 0 && (
                            <div className={s.paginationWrapper}>
                                <Pagination
                                    disabled={loading}
                                    hideOnSinglePage
                                    current={currentPage}
                                    total={total}
                                    pageSize={PAGE_SIZE}
                                    onChange={handlePageChange}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <div className={s.emptyState}>
                        <div className={s.emptyIcon}>
                            <img src={KlarityEmpty} alt="empty" />
                        </div>
                        <h3 className={s.emptyTitle}>No cases found</h3>
                    </div>
                )}
            </Spin>

            <TextVisitSubmittedDrawer
                open={showDetail}
                onClose={() => setShowDetail(false)}
                textVisitUuid={selectedCase?.textVisitUuid}
            />

            <RemindProviderModal
                open={showRemind}
                providerId={remindTarget?.assignedProviderId}
                providerName={remindTarget ? providerName(remindTarget) : ''}
                textVisitId={remindTarget?.id}
                patientName={
                    remindTarget
                        ? [remindTarget.patientFirstName, remindTarget.patientLastName].filter(Boolean).join(' ')
                        : ''
                }
                waitingDays={
                    remindTarget?.patientSubmitAt
                        ? dayjs().diff(dayjs.utc(remindTarget.patientSubmitAt).local(), 'day')
                        : 0
                }
                onClose={() => setShowRemind(false)}
            />
        </div>
    );
};

export default PracticeView;
